<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="storyScroller"
    class="bg-gray-25 relative flex flex-col gap-y-4 md:gap-y-12 pt-20 pb-px md:pb-12"
  >
    <section
      v-if="introduction && (introduction.title || introduction.body || (introduction.cta && introduction.cta.length))"
      class="sm:w-2/3 flex flex-col items-center max-container"
    >
      <h2
        v-if="introduction.title"
        :id="introduction.title?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')"
        class="flex flex-col items-center text-center"
      >
        {{ introduction.title }}
      </h2>
      <div
        v-if="introduction.body"
        class="body-1 text-center lg:max-w-2/3"
        v-html="introduction.body"
      />
      <div
        v-if="introduction.cta && introduction.cta.length"
        class="w-full flex flex-col sm:flex-col md:flex-col lg:flex-row items-center justify-center gap-0 md:gap-4 lg:gap-x-20 [&_.cta-link]:w-full lg:[&_.cta-link]:w-fit"
      >
        <CtaLink
          v-for="(cta, idx) in introduction.cta"
          :key="idx"
          :href="cta.link_url"
          :external="cta.link_external"
          :text="cta.link_text"
        />
      </div>
    </section>
    <section
      ref="scrollContainer"
      class="max-container relative w-full flex flex-row gap-x-0 md:gap-x-8 lg:gap-x-12 items-start py-0 z-20"
    >
      <nav
        ref="navList"
        class="hidden md:flex my-auto transition-all duration-300 ease-out"
        aria-label="Story Scroller pagination"
      >
        <ul
          class="list-none relative -left-2 md:left-0 m-0 mt-0 p-0 flex flex-col flex-1 gap-y-1"
        >
          <li
            v-for="(_section, idx) in sections"
            :key="idx"
          >
            <button
              :aria-current="isActiveSection(idx) ? 'page' : undefined"
              :aria-disabled="isActiveSection(idx) ? true : false"
              :tabindex="isActiveSection(idx) ? '-1' : '0'"
              :class="[
                (isActiveSection(idx) ? 'cursor-auto' : 'cursor-pointer')
              ]"
              class="group p-1.5"
              type="button"
              @click="scrollToSection(idx)"
            >
              <span
                class="pointer-events-none bg-gray-100 block w-1 min-h-full rounded-full transition-all duration-300 ease-in-out"
                :class="[
                  (isActiveSection(idx) === false ? 'group-hover:bg-gray-200 h-10' : 'bg-red-600 h-14'),
                ]"
              >
                <span class="sr-only">Go to story {{ idx + 1 }}</span>
              </span>
            </button>
          </li>
        </ul>
      </nav>
      <div
        class="w-full relative z-10"
        :style="isDesktop ? { minHeight: mediaHeight } : {}"
      >
        <div class="bg-linear-to-t from-transparent via-gray-25/75 backdrop-blur-xs mask-t-from-transparent mask-t-to-black to-gray-25 absolute z-20 top-0 left-0 w-1/2 h-24 hidden md:block"></div>
        <div
          ref="contentScrollContainer"
          class="flex flex-col px-4 md:snap-y md:snap-mandatory md:overflow-y-auto md:no-scrollbar relative"
          :style="isDesktop ? { maxHeight: mediaHeight } : {}"
        >
          <section
            v-for="(section, idx) in sections"
            :id="`story-section-${idx}`"
            :key="idx"
            :ref="(el) => (sectionRefs[idx] = el as HTMLElement)"
            class="md:flex md:flex-col md:justify-center snap-center w-full md:max-w-1/2 first:mt-8 md:first:mt-0 pb-20 md:py-0"
            :style="isDesktop ? { minHeight: sectionHeight } : {}"
          >
            <div
              class="md:m-auto"
            >
              <ResponsiveImage
                v-if="section.media && section.media.type === 'image'"
                :src="section.media?.url"
                :alt="section.media?.alt || section.media?.name"
                :aspect-ratio="mediaRatio"
                :srcset="section.media?.srcset"
                sizes="320px"
                class="mx-auto block md:hidden max-w-xs my-10"
              />
              <ResponsiveVideo
                v-else-if="section.media && section.media.type === 'video' && section.media.video"
                :src="section.media.video.file"
                :aspect-ratio="mediaRatio"
                :autoplay="true"
                :muted="true"
                :loop="true"
                :playsinline="true"
                class="mx-auto block md:hidden max-w-xs my-10"
              />
              <IconOverlay
                v-if="section.icon"
                :icon="section.icon"
                class="mb-4 mx-auto md:mx-1"
              />
              <h3 class="heading-4 text-center md:text-left mb-2">
                {{ section.content?.title }}
              </h3>
              <div
                v-if="section.content?.body"
                class="body-2"
                v-html="section.content.body"
              ></div>
              <CtaLink
                v-if="section.content?.cta"
                :href="section.content?.cta.link_url"
                :external="section.content?.cta.link_external"
                :text="section.content?.cta.link_text"
                class="max-md:flex max-md:flex-col max-md:justify-self-center"
              />
            </div>
          </section>
        </div>
        <div class="bg-linear-to-b from-transparent via-gray-25/75 backdrop-blur-xs mask-b-from-transparent mask-b-to-black to-gray-25 absolute z-20 bottom-0 left-0 w-1/2 h-24 hidden md:block"></div>
      </div>
      <div
        ref="mediaContainer"
        class="hidden md:block absolute top-1/2 -translate-y-1/2 right-4 lg:my-auto w-[calc(50%-6rem)] h-auto max-w-[750px]!"
      >
        <template
          v-for="(section, idx) in sections"
          :key="idx"
        >
          <ResponsiveImage
            v-if="isActiveSection(idx) && section.media && section.media.type === 'image'"
            :src="section.media?.url"
            :alt="section.media?.alt || section.media?.name"
            :aspect-ratio="mediaRatio"
            :srcset="section.media?.srcset"
            sizes="(max-width: 1023px) calc(50vw - 4rem), 750px"
            class="row-start-1 col-start-1 max-w-full animate-[fadeIn_350ms_ease-in-out]"
          />
          <ResponsiveVideo
            v-else-if="isActiveSection(idx) && section.media && section.media.type === 'video' && section.media.video"
            :src="section.media.video.file"
            :poster="section.media.video.thumbnail?.url"
            :aspect-ratio="mediaRatio"
            :autoplay="true"
            :muted="true"
            :loop="true"
            :playsinline="true"
            class="row-start-1 col-start-1 max-w-full animate-[fadeIn_350ms_ease-in-out]"
          />
        </template>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { AspectRatio } from '../../composables/useAspectRatio';
import { computed, nextTick, onBeforeMount, onMounted, onBeforeUnmount, readonly, ref } from 'vue';
import CtaLink from '../CtaLink/CtaLink.vue';
import IconOverlay from '../IconOverlay/IconOverlay.vue';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage.vue';
import ResponsiveVideo from '../ResponsiveVideo/ResponsiveVideo.vue';

type StoryMediaType = 'image' | 'video';

interface StoryScrollerCta {
  link_text: string;
  link_url: string;
  link_external: boolean;
}

interface StoryScrollerIntroduction {
  title: string;
  body: string;
  cta: Array<StoryScrollerCta> | null;
}

interface StoryImage {
  url?: string;
  name?: string;
  alt?: string;
  srcset?: string;
  width?: number;
}

interface StoryVideo {
  video?: {
    title: string;
    caption: string;
    file: string;
    url: string;
    filename: string;
    type: string;
    duration: number;
    width: number | null;
    height: number | null;
    thumbnail: {
      url: string | null;
    } | null;
  }
}

interface StoryMedia extends StoryImage, StoryVideo {
  type: StoryMediaType;
}

interface StoryContent {
  title: string;
  body: string;
  cta: StoryScrollerCta | null;
}

interface Story {
  icon: string | null;
  content: StoryContent;
  media: StoryMedia;
}

interface StoryScroller {
  introduction: StoryScrollerIntroduction;
  stories: Array<Story>;
  uuid: string;
}

const props = defineProps<StoryScroller>();


// ============================================================================
// Refs
// ============================================================================

// --- DOM Element Refs ---
/** Root element for the StoryScroller component (outer div) */
const rootElement = ref<HTMLElement | null>(null);
/** Scrollable max-container for sections (used for scroll/positioning) */
const scrollContainer = ref<HTMLDivElement | null>(null);
/** Array of section DOM elements (for positioning, navigation, etc.) */
const sectionRefs = ref<(HTMLElement)[]>([]);
/** Navigation list element (pagination dots) */
const navList = ref<HTMLElement | null>(null);
/** Media max-container element (for fixed media positioning) */
const mediaContainer = ref<HTMLElement | null>(null);

// --- Miscellaneous/Reactive State ---
/** Current aspect ratio for media (used for sizing) */
const mediaRatio = readonly(ref<AspectRatio>('square'));
/** Index of currently active section */
const activeSection = ref(0);
/** Ref to the scrollable content container */
const contentScrollContainer = ref<HTMLElement | null>(null);
/** Flag to prevent scroll listener from interfering with programmatic scrolling */
const isScrollingProgrammatically = ref(false);
/** Whether the viewport is at the md breakpoint or wider */
const isDesktop = ref(false);

// ============================================================================
// Computed Properties
// ============================================================================

const sections = computed(() => props.stories ?? []);
const mediaHeight = ref<string>('fit-content');
const sectionHeight = ref<string>('fit-content');

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculates and updates the media height based on current viewport and media container.
 * Only applies on desktop (md+); mobile uses natural stacking layout.
 */
function updateMediaHeight(): void {
  isDesktop.value = window.innerWidth >= 768;

  if (!isDesktop.value) return;
  if (!mediaContainer.value || !contentScrollContainer.value) return;

  if ((contentScrollContainer.value.scrollHeight / 3) > mediaContainer.value.clientHeight - 32) {
    mediaHeight.value = `${mediaContainer.value.clientHeight + 64}px`;
    sectionHeight.value = `${mediaContainer.value.clientHeight + 64}px`;
  } else {
    mediaHeight.value = `${mediaContainer.value.clientHeight}px`;
    sectionHeight.value = `${mediaContainer.value.clientHeight}px`;
  }
}

/**
 * Determines if the given section index is currently active.
 */
const isActiveSection = (idx: number): boolean => idx === activeSection.value;

// ============================================================================
// Scroll Tracking
// ============================================================================

/**
 * Determines which section is closest to the center of the content container
 * and updates the active section accordingly.
 * This only considers the scroll position within the contentScrollContainer,
 * not the overall page scroll position.
 */
function updateActiveSectionFromScroll(): void {
  // Don't update during programmatic scrolling
  if (isScrollingProgrammatically.value) return;

  // Ensure we have a content container to work with
  if (!contentScrollContainer.value) return;

  // Get the content container's bounding rect to find its center
  const containerRect = contentScrollContainer.value.getBoundingClientRect();
  const containerCenterY = containerRect.top + containerRect.height / 2;

  let closestIndex = 0;
  let closestDistance = Infinity;

  // Find which section is closest to the container's center
  sectionRefs.value.forEach((section, idx) => {
    if (!section) return;

    const rect = section.getBoundingClientRect();
    // Calculate the center of the section
    const sectionCenterY = rect.top + rect.height / 2;
    // Calculate distance from container center (not viewport center)
    const distance = Math.abs(sectionCenterY - containerCenterY);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = idx;
    }
  });

  // Update active section if it changed
  if (activeSection.value !== closestIndex) {
    activeSection.value = closestIndex;
  }
}

/**
 * Handles scroll events on the content container only.
 * Uses requestAnimationFrame for smooth performance.
 */
let scrollRefId: number | null = null;
function handleContentScroll(): void {
  if (scrollRefId !== null) {
    cancelAnimationFrame(scrollRefId);
  }

  scrollRefId = requestAnimationFrame(() => {
    updateActiveSectionFromScroll();
    scrollRefId = null;
  });
}

/**
 * Handles wheel events for scroll chaining behavior.
 * When the content container reaches top/bottom, allows page scrolling to continue.
 * Only active on desktop where the container has constrained scroll.
 */
function handleWheelEvent(e: WheelEvent): void {
  if (!isDesktop.value) return;
  const target = e.currentTarget as HTMLElement;
  if (!target) return;

  const scrollTop = target.scrollTop;
  const scrollHeight = target.scrollHeight;
  const clientHeight = target.clientHeight;
  const delta = e.deltaY;

  const isAtTop = scrollTop <= 1;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
  const isScrollingUp = delta < 0;
  const isScrollingDown = delta > 0;

  // If at top and scrolling up, allow page scroll instead
  if (isAtTop && isScrollingUp) {
    e.preventDefault();
    window.scrollBy({
      top: delta,
      behavior: 'instant'
    });
    return;
  }

  // If at bottom and scrolling down, allow page scroll instead
  if (isAtBottom && isScrollingDown) {
    e.preventDefault();
    window.scrollBy({
      top: delta,
      behavior: 'instant'
    });
    return;
  }

  // Otherwise allow normal container scrolling (don't prevent)
}

// ============================================================================
// Navigation Functions
// ============================================================================

/**
 * Handles window resize events.
 * Uses requestAnimationFrame for smooth performance.
 */
let resizeRefId: number | null = null;
function handleResize(): void {
  if (resizeRefId !== null) {
    cancelAnimationFrame(resizeRefId);
  }

  resizeRefId = requestAnimationFrame(() => {
    updateMediaHeight();
    resizeRefId = null;
  });
}

/**
 * Scrolls to the specified section index using HTML anchors.
 * Updates active section and scrolls to the section.
 * @param idx Index of the section to scroll to
 */
const scrollToSection = (idx: number) => {
  // Find the section element by its unique ID
  const sectionId = `story-section-${idx}`;
  const section = document.getElementById(sectionId);

  // Ensure we have a valid section
  if (!section) return;
  // Immediately mark the section active
  activeSection.value = idx;
  // Set flag to prevent scroll listener from interfering
  isScrollingProgrammatically.value = true;

  nextTick(() => {
    // Use scrollIntoView for smooth anchor-based scrolling
    section.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    // Reset flag after scrolling completes (wait for animation)
    setTimeout(() => {
      isScrollingProgrammatically.value = false;
    }, 1000);
  });
}

// ============================================================================
// Lifecycle Hooks
// ============================================================================
onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json'];
});

onMounted(() => {
  nextTick(() => {
    // ONLY listen to scroll events on the content container
    // Do NOT listen to window scroll - we only care about container scroll position
    if (contentScrollContainer.value) {
      contentScrollContainer.value.addEventListener('scroll', handleContentScroll, { passive: true });
      // Add wheel event listener for scroll chaining behavior
      contentScrollContainer.value.addEventListener('wheel', handleWheelEvent, { passive: false });
    }

    // Add resize listener to update media height
    window.addEventListener('resize', handleResize, { passive: true });

    // Initialize media height
    updateMediaHeight();
  });
});

onBeforeUnmount(() => {
  // Clean up scroll listeners (no window scroll listener anymore)
  if (contentScrollContainer.value) {
    contentScrollContainer.value.removeEventListener('scroll', handleContentScroll);
    contentScrollContainer.value.removeEventListener('wheel', handleWheelEvent);
  }

  // Clean up resize listener
  window.removeEventListener('resize', handleResize);

  // Clean up animation frames if pending
  if (scrollRefId !== null) {
    cancelAnimationFrame(scrollRefId);
  }
  if (resizeRefId !== null) {
    cancelAnimationFrame(resizeRefId);
  }
});
</script>
